import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API } from '../context/AdminAuthContext';
import { Wrench } from 'lucide-react';

/**
 * MaintenanceGate — three INDEPENDENT locks (Feb 2026):
 *
 *  1. Storefront lock (`enabled`)      → hides the customer storefront.
 *  2. Branch-portal lock (`branch.enabled`) → hides /branch* routes only.
 *
 * Flipping one has ZERO effect on the other. Admin (/admin*), driver
 * (/driver*), warehouse floor mode (/warehouse*), and public tracking
 * (/track/*) routes always pass through — trackers must stay reachable
 * for customers + branches whose orders are already in transit, and
 * the warehouse floor (Picker / Put-away scanner) must stay usable
 * during storefront maintenance windows so operators can keep working.
 */
const ALWAYS_PASS_PREFIXES = ['/admin', '/driver', '/track', '/warehouse', '/wms'];

export const MaintenanceGate = ({ children }) => {
  const [state, setState] = useState({
    loaded: false,
    store: { enabled: false, message: '' },
    branch: { enabled: false, message: '' },
  });
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        // Feb 2026 — Super-admin bypass. If a super admin JWT is sat
        // in localStorage (same key the admin shell uses) attach it as
        // a bearer; the backend returns `enabled: false` for super
        // admins so both the storefront + branch portal stay reachable
        // during a planned maintenance window.
        const headers = {};
        try {
          const tok = window.localStorage?.getItem('mr_admin_token');
          if (tok) headers.Authorization = `Bearer ${tok}`;
        } catch (_) { /* non-browser SSR path */ }
        const { data } = await axios.get(`${API}/maintenance`, { timeout: 6000, headers });
        if (!alive) return;
        setState({
          loaded: true,
          store: { enabled: !!data.enabled, message: data.message || '' },
          branch: {
            enabled: !!(data.branch && data.branch.enabled),
            message: (data.branch && data.branch.message) || '',
          },
        });
      } catch (e) {
        if (!alive) return;
        // Never block anyone on a failed check — fail-open.
        setState({ loaded: true, store: { enabled: false, message: '' }, branch: { enabled: false, message: '' } });
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Admin + driver routes ALWAYS pass so super admin can toggle locks off.
  const isAlwaysPass = ALWAYS_PASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const isBranchRoute = pathname === '/branch' || pathname.startsWith('/branch/');

  if (!state.loaded || isAlwaysPass) return children;

  // On /branch* → only the branch-portal lock applies. Storefront lock
  // must never spill over onto the branch portal.
  if (isBranchRoute) {
    if (!state.branch.enabled) return children;
    return <LockScreen scope="branch" message={state.branch.message} />;
  }

  // Elsewhere (storefront) → only the storefront lock applies.
  if (!state.store.enabled) return children;
  return <LockScreen scope="store" message={state.store.message} />;
};

const LockScreen = ({ scope, message }) => (
  <div
    className="min-h-screen bg-gradient-to-br from-[#0A1B3D] via-[#0B1E47] to-[#061226] flex items-center justify-center p-6"
    data-testid={scope === 'branch' ? 'branch-maintenance-screen' : 'maintenance-screen'}
  >
    <div className="max-w-md w-full text-center text-white">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/10 backdrop-blur ring-1 ring-white/15 mb-7">
        <Wrench className="w-9 h-9 text-amber-300" strokeWidth={1.8} />
      </div>
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 mb-3">
        Scheduled Maintenance
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1] mb-4">
        We&apos;ll be back shortly.
      </h1>
      <p className="text-white/70 text-sm leading-relaxed">
        {message
          || (scope === 'branch'
            ? 'The branch portal is temporarily offline while we make a few improvements. Please check back in a few minutes.'
            : 'The store is temporarily offline while we make a few improvements. Please check back in a few minutes.')}
      </p>
      <div className="mt-8 inline-flex items-center gap-2 text-[11px] text-white/40 font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
        This page refreshes automatically
      </div>
    </div>
  </div>
);

export default MaintenanceGate;
